<template>
  <!-- 客户概况：客户资产、结构分布与增长趋势总览 -->
  <div class="customer-overview">
    <list-page>
      <list-table background-color="#FFF">
        <div v-loading="loading" class="ov-body">
          <!-- KPI -->
          <div class="stat-grid">
            <div class="stat-card">
              <div class="label">客户总数</div>
              <div class="value">{{ overview.customerCount || 0 }}<span class="unit">家</span></div>
              <div class="sub">全部客户</div>
            </div>
            <div class="stat-card">
              <div class="label">本月新增客户</div>
              <div class="value">{{ overview.monthNewCount || 0 }}<span class="unit">家</span></div>
              <div class="sub">{{ currentMonth }} 入库</div>
            </div>
            <div class="stat-card">
              <div class="label">活跃客户</div>
              <div class="value success">{{ overview.activeCount || 0 }}<span class="unit">家</span></div>
              <div class="sub">订阅预警状态为正常</div>
            </div>
            <div class="stat-card">
              <div class="label">流失风险客户</div>
              <div class="value danger">{{ overview.riskCount || 0 }}<span class="unit">家</span></div>
              <div class="sub">订阅预警状态为已过期</div>
            </div>
          </div>

          <!-- 客户类型 / 客户来源 -->
          <div class="grid-2">
            <div class="chart-card">
              <div class="section-title">客户类型分布</div>
              <div class="donut-wrap">
                <div ref="typeChart" class="donut-div"></div>
                <div class="donut-legend">
                  <div v-for="row in typeRows" :key="row.name" class="lg-row">
                    <span class="lg-dot" :style="{background: row.color}"></span>
                    <span class="lg-name" :title="row.name">{{ row.name }}</span>
                    <span class="lg-val">{{ row.value }}</span>
                    <span class="lg-ratio">{{ row.ratio }}%</span>
                  </div>
                  <div v-if="!typeRows.length" class="lg-empty">暂无数据</div>
                </div>
              </div>
            </div>
            <div class="chart-card">
              <div class="section-title">客户来源分布</div>
              <div class="donut-wrap">
                <div ref="sourceChart" class="donut-div"></div>
                <div class="donut-legend">
                  <div v-for="row in sourceRows" :key="row.name" class="lg-row">
                    <span class="lg-dot" :style="{background: row.color}"></span>
                    <span class="lg-name" :title="row.name">{{ row.name }}</span>
                    <span class="lg-val">{{ row.value }}</span>
                    <span class="lg-ratio">{{ row.ratio }}%</span>
                  </div>
                  <div v-if="!sourceRows.length" class="lg-empty">暂无数据</div>
                </div>
              </div>
            </div>
          </div>

          <!-- 增长趋势：按入库时间（created_time）归月 -->
          <div class="chart-card">
            <div class="section-title">客户增长趋势</div>
            <div ref="trendChart" class="chart-div"></div>
          </div>

          <!-- 行业 / 区域 -->
          <div class="grid-2">
            <div class="chart-card">
              <div class="section-title">行业分布 TOP</div>
              <div ref="industryChart" class="hbar-div"></div>
            </div>
            <div class="chart-card">
              <div class="section-title">区域分布</div>
              <div ref="regionChart" class="hbar-div"></div>
            </div>
          </div>

          <!-- 客户价值排行 TOP10 -->
          <div class="chart-card">
            <div class="section-title">客户价值排行 TOP10</div>
            <el-table :data="overview.topList || []" border size="small" empty-text="暂无数据">
              <el-table-column label="排名" width="70" align="center">
                <template v-slot="{$index}">
                  <span class="rank-no" :class="'rank-' + ($index + 1)">{{ $index + 1 }}</span>
                </template>
              </el-table-column>
              <el-table-column label="客户名称" prop="customerName" min-width="200" show-overflow-tooltip/>
              <el-table-column label="客户类型" prop="customerType" width="120">
                <template v-slot="{row}">{{ dictName(customerTypeList, row.customerType) || '—' }}</template>
              </el-table-column>
              <el-table-column label="所属行业" prop="industryId" width="120">
                <template v-slot="{row}">{{ dictName(industryList, row.industryId) || '—' }}</template>
              </el-table-column>
              <el-table-column label="负责人" prop="ownerEmployeeName" width="100">
                <template v-slot="{row}">{{ row.ownerEmployeeName || '—' }}</template>
              </el-table-column>
              <el-table-column label="累计金额(元)" prop="accumulatedAmount" align="right" width="150">
                <template v-slot="{row}"><span class="num bold">{{ money(row.accumulatedAmount) }}</span></template>
              </el-table-column>
              <el-table-column label="最近跟进" prop="lastFollowDate" width="120">
                <template v-slot="{row}">{{ dateOnly(row.lastFollowDate) }}</template>
              </el-table-column>
            </el-table>
          </div>
        </div>
      </list-table>
    </list-page>
  </div>
</template>

<script>
import * as echarts from "echarts";
import {markRaw} from "vue";
import ListPage from "@/components/h5/layout/ListPage.vue";
import ListTable from "@/components/h5/layout/ListTable.vue";
import SearchCondition from "@/components/h5/layout/SearchCondition.vue";
import PageButton from "@/components/h5/button/PageButton.vue";
import customerStatApi from "@/api/dz-crm/partner/customerStatApi";
import commonApi from "@/api/commonApi";
import {DICT_CUSTOMER_TYPE, DICT_INDUSTRY_TYPE, DICT_PARTNER_ORIGIN, dictName} from "@/views/h5/crm/crmDictionary";
import {provinceName, toChartRows} from "@/views/h5/crm/customerStat";
import {useRoute, useRouter} from "vue-router";

export default {
  setup() {
    return {router: useRouter(), route: useRoute()};
  },
  name: "CustomerOverview",
  components: {ListPage, ListTable, SearchCondition, PageButton},
  data() {
    return {
      loading: false,
      overview: {},
      typeChart: null,
      sourceChart: null,
      trendChart: null,
      industryChart: null,
      regionChart: null,
      // 字典：后端只返回值，名称由前端关联（规则12）
      customerTypeList: [],
      industryList: [],
      sourceList: [],
      dictReady: false,
      dataReady: false,
    };
  },
  computed: {
    currentMonth() {
      const now = new Date();
      return now.getFullYear() + "-" + String(now.getMonth() + 1).padStart(2, "0");
    },
    typeRows() {
      return toChartRows(this.overview.typeStats, this.customerTypeList);
    },
    sourceRows() {
      return toChartRows(this.overview.sourceStats, this.sourceList);
    },
    industryRows() {
      return toChartRows(this.overview.industryStats, this.industryList);
    },
    /** 区域维度存的是行政区划前两位，按省名渲染 */
    regionRows() {
      return toChartRows(this.overview.regionStats, null, provinceName);
    },
  },
  beforeRouteLeave(to, from, next) {
    // 只读的统计页，不缓存页面数据
    next();
  },
  mounted() {
    // 字典与数据都到齐才画图，否则字典还没回来就把阶段/类型画成了空名字
    Promise.all([
      commonApi.queryUniqueDictionaryList(DICT_CUSTOMER_TYPE),
      commonApi.queryUniqueDictionaryList(DICT_INDUSTRY_TYPE),
      commonApi.queryUniqueDictionaryList(DICT_PARTNER_ORIGIN),
    ]).then(([customerType, industry, source]) => {
      this.customerTypeList = customerType || [];
      this.industryList = industry || [];
      this.sourceList = source || [];
      this.dictReady = true;
      this.renderIfReady();
    });
    this.loadOverview();
  },
  unmounted() {
    [this.typeChart, this.sourceChart, this.trendChart, this.industryChart, this.regionChart]
      .forEach(chart => chart && chart.dispose());
  },
  methods: {
    dictName,
    async loadOverview() {
      this.loading = true;
      try {
        this.overview = await customerStatApi.overview({}) || {};
        this.dataReady = true;
        this.$nextTick(() => this.renderIfReady());
      } finally {
        this.loading = false;
      }
    },
    renderIfReady() {
      if (this.dictReady && this.dataReady) {
        this.renderCharts();
      }
    },
    renderCharts() {
      this.typeChart = this.renderDonut(this.typeChart, this.$refs.typeChart, this.typeRows, this.overview.customerCount);
      this.sourceChart = this.renderDonut(this.sourceChart, this.$refs.sourceChart, this.sourceRows,
        this.sourceRows.reduce((sum, row) => sum + row.value, 0));
      this.trendChart = this.renderLine(this.trendChart, this.$refs.trendChart,
        this.overview.trendMonthList || [], this.overview.trendCountList || []);
      this.industryChart = this.renderHBar(this.industryChart, this.$refs.industryChart, this.industryRows);
      this.regionChart = this.renderHBar(this.regionChart, this.$refs.regionChart, this.regionRows);
    },
    /** 环形图：图例不用 echarts 自带的，改用右侧带数量与占比的自定义列表 */
    renderDonut(chart, dom, rows, centerValue) {
      if (!dom) {
        return chart;
      }
      if (!chart) {
        chart = markRaw(echarts.init(dom));
      }
      chart.setOption({
        tooltip: {trigger: "item", appendToBody: true, formatter: "{b}：{c} ({d}%)"},
        title: {
          text: String(centerValue || 0),
          subtext: "客户数量",
          left: "center",
          top: "center",
          textStyle: {fontSize: 22, fontWeight: 600, color: "#1f2329"},
          subtextStyle: {fontSize: 11, color: "#8d97a6"},
        },
        series: [{
          type: "pie",
          radius: ["58%", "80%"],
          center: ["50%", "50%"],
          avoidLabelOverlap: false,
          label: {show: false},
          labelLine: {show: false},
          itemStyle: {borderColor: "#fff", borderWidth: 2},
          data: rows.map(row => ({name: row.name, value: row.value, itemStyle: {color: row.color}})),
        }],
      });
      return chart;
    },
    /** 横向条形图：行业/区域 TOP，大的排在上面 */
    renderHBar(chart, dom, rows) {
      if (!dom) {
        return chart;
      }
      if (!chart) {
        chart = markRaw(echarts.init(dom));
      }
      // y 轴类目从下往上排，倒序后最大值才落在顶部
      const ordered = rows.slice().reverse();
      chart.setOption({
        tooltip: {trigger: "axis", appendToBody: true, axisPointer: {type: "shadow"}},
        grid: {left: "0", right: "36", bottom: "3%", top: "3%", containLabel: true},
        xAxis: {type: "value", splitLine: {lineStyle: {color: "#eee"}}, axisLabel: {color: "#909399"}},
        yAxis: {
          type: "category",
          data: ordered.map(row => row.name),
          axisTick: {show: false},
          axisLabel: {color: "#909399"},
        },
        series: [{
          type: "bar",
          barWidth: "50%",
          itemStyle: {color: "#1677ff", borderRadius: [0, 3, 3, 0]},
          label: {show: true, position: "right", color: "#606266", fontSize: 11},
          data: ordered.map(row => row.value),
        }],
      });
      return chart;
    },
    renderLine(chart, dom, xNames, data) {
      if (!dom) {
        return chart;
      }
      if (!chart) {
        chart = markRaw(echarts.init(dom));
      }
      chart.setOption({
        tooltip: {trigger: "axis", appendToBody: true},
        grid: {left: "0", right: "0", bottom: "3%", top: "10%", containLabel: true},
        xAxis: {type: "category", boundaryGap: false, data: xNames, axisLabel: {color: "#909399"}},
        yAxis: {type: "value", minInterval: 1, splitLine: {lineStyle: {color: "#eee"}}, axisLabel: {color: "#909399"}},
        series: [{type: "line", smooth: true, itemStyle: {color: "#1677ff"}, areaStyle: {opacity: 0.08}, data}],
      });
      return chart;
    },
    dateOnly(value) {
      return value ? String(value).slice(0, 10) : "—";
    },
    money(value) {
      if (value === null || value === undefined || value === "") {
        return "0.00";
      }
      return Number(value).toLocaleString("zh-CN", {minimumFractionDigits: 2, maximumFractionDigits: 2});
    },
  },
};
</script>

<style lang="scss" scoped>
.customer-overview {
  height: 100%;

  .ov-body {
    padding: 10px 0;
  }

  // KPI 卡片
  .stat-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 12px;
    margin-bottom: 12px;
  }

  .stat-card {
    border: 1px solid var(--yq-border-color, #ebeef5);
    border-radius: 6px;
    padding: 14px 16px;
    background: #fff;

    .label {
      font-size: 13px;
      color: #909399;
    }

    .value {
      margin-top: 6px;
      font-size: 26px;
      font-weight: 600;
      color: #1f2329;
      line-height: 1.2;

      &.success {
        color: #2e8b57;
      }

      &.danger {
        color: #c0392b;
      }

      .unit {
        margin-left: 4px;
        font-size: 13px;
        font-weight: 400;
        color: #909399;
      }
    }

    .sub {
      margin-top: 4px;
      font-size: 12px;
      color: #c0c4cc;
    }
  }

  .grid-2 {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(420px, 1fr));
    gap: 12px;
  }

  .chart-card {
    border: 1px solid var(--yq-border-color, #ebeef5);
    border-radius: 6px;
    padding: 12px 16px;
    margin-bottom: 12px;
    background: #fff;

    .section-title {
      font-size: 14px;
      font-weight: 600;
      color: #303133;
      margin-bottom: 10px;
    }
  }

  .chart-div {
    height: 280px;
  }

  .hbar-div {
    height: 260px;
  }

  // 环形图 + 右侧自定义图例（带数量与占比）
  .donut-wrap {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .donut-div {
    flex: none;
    width: 200px;
    height: 200px;
  }

  .donut-legend {
    flex: 1;
    min-width: 0;
    max-height: 200px;
    overflow-y: auto;

    .lg-row {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 4px 0;
      font-size: 12px;
      color: #606266;

      .lg-dot {
        flex: none;
        width: 8px;
        height: 8px;
        border-radius: 50%;
      }

      .lg-name {
        flex: 1;
        min-width: 0;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }

      .lg-val {
        flex: none;
        font-variant-numeric: tabular-nums;
        font-weight: 600;
        color: #303133;
      }

      .lg-ratio {
        flex: none;
        width: 52px;
        text-align: right;
        color: #909399;
        font-variant-numeric: tabular-nums;
      }
    }

    .lg-empty {
      padding: 8px 0;
      font-size: 12px;
      color: #c0c4cc;
    }
  }

  .rank-no {
    display: inline-block;
    width: 20px;
    height: 20px;
    line-height: 20px;
    border-radius: 50%;
    background: #f0f3f8;
    color: #606266;
    font-size: 12px;

    &.rank-1 {
      background: #faad14;
      color: #fff;
    }

    &.rank-2 {
      background: #b7c0cd;
      color: #fff;
    }

    &.rank-3 {
      background: #d8a06a;
      color: #fff;
    }
  }

  .num {
    font-variant-numeric: tabular-nums;
  }

  .bold {
    font-weight: 600;
    color: #303133;
  }
}
</style>
